import { Badge } from "@/components/ui/Badge";
import { useAnalytics } from "@/features/analytics/context/AnalyticsProvider";
import type { CitationRoute, CitationSourceCta } from "@/features/analytics/types/events";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { useEffect, useMemo, useRef } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useDocumentQuery } from "../api/queries";
import { ContextExpander } from "../components/ContextExpander";
import { FdaPdfViewer } from "../components/FdaPdfViewer";
import { MarkdownLine } from "../components/MarkdownLine";
import { PatentMetadataPanel } from "../components/PatentMetadataPanel";
import { ExtractedTableBlock, TableBlock } from "../components/TableBlock";
import { useTrackCitationView } from "../hooks/useTrackCitationView";
import type { ClassifiedLine, DocumentSourceType, PaperMeta } from "../types";
import { HEADER_ROLES } from "../types";
import { classifyLine } from "../utils/classifyLine";
import { formatAuthors } from "../utils/formatAuthors";
import { groupTableLines } from "../utils/groupTableLines";
import { parseHighlights } from "../utils/parseHighlights";
import { scrollToTarget } from "../utils/scrollToTarget";

function getDocumentTitle(meta: PaperMeta, lines?: { text: string; num?: number }[], sourceType?: string): string {
  if (meta.title || meta.doc_title || meta.official_title) {
    return (meta.title || meta.doc_title || meta.official_title)!;
  }
  if (meta.tradename) return meta.tradename;

  if (sourceType === "fda" && lines) {
    for (const line of lines.slice(0, 15)) {
      const m = line.text.match(/(?:Trade(?:\/Device)?\s*Name)[:\s]*(.+)/i);
      if (m) return m[1].trim();
    }
    const id = meta.identifier || "";
    if (id.startsWith("K")) return `510(k) ${id}`;
    if (id.startsWith("BLA")) return `${id} Approval`;
    if (id.startsWith("P")) return `PMA ${id}`;
    if (id) return id;
  }

  if (lines && lines.length > 0) {
    const first = lines[0].text.trim();
    if (!/seal of the|food and drug|u\.?s\.?\s*f/i.test(first)) return first;
    if (lines.length > 1) return lines[1].text.trim();
  }
  return "";
}

function getSourceBadge(sourceType?: DocumentSourceType, meta?: PaperMeta): string {
  if (sourceType === "fda") return "FDA";
  if (sourceType === "trials") return "CLINICAL TRIAL";
  if (sourceType === "patents") return "PATENT";
  if (sourceType === "sec") return "SEC";
  const src = meta?.source?.toUpperCase();
  if (src) return src;
  return "";
}

function getFdaAppNumber(meta: PaperMeta, lines?: { num: number; text: string }[]): string | null {
  const urlStr = meta.public_url || "";
  // accessdata review paths only — skip GCS UUIDs and /media/ IDs
  const urlMatch = urlStr.match(
    /drugsatfda_docs\/[^/]+\/(?:\d{4}\/)?(\d{5,6}(?:Orig\d+)?(?:s\d+)?)/i,
  );
  if (urlMatch) return urlMatch[1];

  if (lines && lines.length >= 3) {
    const l2 = lines.find((l) => l.num === 2);
    const l3 = lines.find((l) => l.num === 3);
    if (l2?.text.match(/application\s+number/i) && l3) {
      return l3.text.trim();
    }
  }
  return null;
}

function formatFdaDocType(sourceType?: string): string | null {
  if (!sourceType) return null;
  const map: Record<string, string> = {
    toc_review: "Review Document",
    label: "Drug Label",
    "510k": "510(k) Clearance",
    pma: "PMA Approval",
    denovo: "De Novo Classification",
    guidance: "Guidance Document",
    warning_letter: "Warning Letter",
  };
  return map[sourceType] || sourceType.replace(/_/g, " ");
}

function getOriginalUrl(meta: PaperMeta, docId: string, sourceType?: DocumentSourceType): string | null {
  if (meta.public_url) return meta.public_url;

  if (sourceType === "trials" || meta.nct_id) {
    const nct = meta.nct_id || meta.identifier || docId;
    if (nct.startsWith("NCT")) return `https://clinicaltrials.gov/study/${nct}`;
    return null;
  }

  if (sourceType === "patents") {
    if (meta.google_patents_url) return meta.google_patents_url;
    const pub = meta.publication_number || meta.pub_number || docId;
    const cleaned = pub.replace(/-/g, "");
    // US pre-grant pubs only: US + YYYY + serial (pad serial to 7) + kind
    // e.g. US20200148718A1. Do NOT apply to granted patents like US9585906B2 —
    // matching those as year=9585 + serial=906 → pad → US95850000906B2 (404).
    const m = cleaned.match(/^(US)((?:19|20)\d{2})(\d+)([A-Z]\d+)$/);
    if (m) {
      const [, country, year, serial, kind] = m;
      return `https://patents.google.com/patent/${country}${year}${serial.padStart(7, "0")}${kind}`;
    }
    return `https://patents.google.com/patent/${cleaned}`;
  }

  const source = meta.source?.toLowerCase();
  if (source === "arxiv") {
    const arxivId = meta.document_id || docId.replace(/^arx_/, "");
    return `https://arxiv.org/abs/${arxivId}`;
  }
  if (source === "pmc") {
    const pmcId = meta.pmc_id || meta.document_id || docId;
    return `https://www.ncbi.nlm.nih.gov/pmc/articles/${pmcId}/`;
  }
  if (meta.doi) return `https://doi.org/${meta.doi}`;

  return null;
}

function getSourceLabel(sourceType?: DocumentSourceType, meta?: PaperMeta): string {
  if (sourceType === "fda") return "FDA";
  if (sourceType === "trials") {
    if (meta?.nct_id || meta?.identifier?.startsWith("NCT")) return "ClinicalTrials.gov";
    return "Clinical Trial Registry";
  }
  if (sourceType === "patents") return "Google Patents";
  switch (meta?.source?.toLowerCase()) {
    case "arxiv": return "arXiv";
    case "pmc": return "PubMed Central";
    case "biorxiv": return "bioRxiv";
    case "medrxiv": return "medRxiv";
    default: return meta?.source || "source";
  }
}


interface DocumentViewerPageProps {
  source: CitationRoute;
  docIdProp?: string;
  highlightsProp?: Set<number>;
}

export const DocumentViewerPage = ({ source, docIdProp, highlightsProp }: DocumentViewerPageProps) => {
  const { docId: routeDocId } = useParams<{ docId: string }>();
  const location = useLocation();

  const docId = docIdProp ?? routeDocId;
  const { trackEvent } = useAnalytics();
  const { user } = useAuth();
  const firstHighlightRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const highlights = useMemo(
    () => highlightsProp ?? parseHighlights(location.hash),
    [highlightsProp, location.hash]
  );

  const highlightLines = useMemo(
    () => [...highlights].sort((a, b) => a - b),
    [highlights],
  );

  const {
    data,
    isPending: loading,
    error: documentError,
  } = useDocumentQuery(source, docId, highlightLines);
  const error =
    documentError instanceof Error
      ? documentError.message
      : documentError
        ? String(documentError)
        : null;

  const matchedHighlights = useMemo(() => {
    if (!data) return new Set<number>();
    const lineNums = new Set(data.lines.map((l) => l.num));
    return new Set([...highlights].filter((n) => lineNums.has(n)));
  }, [data, highlights]);

  const unmatchedHighlights = useMemo(
    () => [...highlights].filter((n) => !matchedHighlights.has(n)).sort((a, b) => a - b),
    [highlights, matchedHighlights]
  );

  const hasCitations = matchedHighlights.size > 0;

  useEffect(() => {
    if (data && firstHighlightRef.current && scrollContainerRef.current) {
      setTimeout(() => {
        scrollToTarget(scrollContainerRef.current, firstHighlightRef.current);
      }, 100);
    }
  }, [data, highlights]);

  useTrackCitationView(source, docId, data ?? null, highlights.size);

  const classified: ClassifiedLine[] = useMemo(() => {
    if (!data) return [];
    const title = getDocumentTitle(data.meta, data.lines, data.source_type);
    return data.lines.map((line, i) => ({
      ...line,
      role: classifyLine(line, i, data.lines, title),
    }));
  }, [data]);

  const firstHighlight = useMemo(
    () => [...matchedHighlights].sort((a, b) => a - b)[0],
    [matchedHighlights]
  );

  const renderLine = (line: ClassifiedLine, highlighted: boolean): React.ReactNode => {
    const isFirst = highlighted && line.num === firstHighlight;

    const highlightClasses = highlighted
      ? "bg-amber-50 border-l-[3px] border-l-amber-400 pl-4 -ml-4 rounded-r"
      : "";

    switch (line.role) {
      case "major_heading":
        return (
          <h2
            id={`L${line.num}`}
            ref={isFirst ? firstHighlightRef : undefined}
            className={`text-2xl font-medium tracking-tight text-zinc-900 mt-10 mb-3 pb-1.5 border-b border-zinc-200 ${highlightClasses}`}
          >
            {line.text}
          </h2>
        );

      case "minor_heading":
        return (
          <h3
            id={`L${line.num}`}
            ref={isFirst ? firstHighlightRef : undefined}
            className={`text-base font-medium text-zinc-800 mt-7 mb-2 ${highlightClasses}`}
          >
            {line.text}
          </h3>
        );

      case "figure_caption":
        return (
          <div
            id={`L${line.num}`}
            ref={isFirst ? firstHighlightRef : undefined}
            className={`my-4 ${highlightClasses}`}
          >
            <p className="text-sm text-zinc-500 leading-relaxed italic px-4">
              {line.text}
            </p>
          </div>
        );

      case "table_caption": {
        const [caption, ...embeddedLines] = line.text.split("\n");
        return (
          <div
            id={`L${line.num}`}
            ref={isFirst ? firstHighlightRef : undefined}
            className={`my-4 ${highlightClasses}`}
          >
            <p className="text-sm text-zinc-500 leading-relaxed italic px-4">
              {caption}
            </p>
            {line.role === "table_caption" && embeddedLines.length > 0 && (
              <ExtractedTableBlock lines={embeddedLines} />
            )}
          </div>
        );
      }

      case "reference":
        return (
          <p
            id={`L${line.num}`}
            ref={isFirst ? firstHighlightRef : undefined}
            className={`text-sm text-zinc-500 leading-relaxed pl-6 -indent-6 mb-1 ${highlightClasses}`}
          >
            {line.text}
          </p>
        );

      case "abstract":
        return (
          <p
            id={`L${line.num}`}
            ref={isFirst ? firstHighlightRef : undefined}
            className={`text-base text-zinc-600 leading-relaxed mb-3 italic ${highlightClasses}`}
          >
            {line.text}
          </p>
        );

      default:
        return (
          <div
            id={`L${line.num}`}
            ref={isFirst ? firstHighlightRef : undefined}
            className={`text-base text-zinc-800 leading-[1.7] mb-3 ${highlightClasses}`}
          >
            <MarkdownLine content={line.text} />
          </div>
        );
    }
  };

  const renderGroupedLines = (lines: ClassifiedLine[], highlighted: boolean): React.ReactNode[] => {
    const groups = groupTableLines(lines);
    return groups.map((group) => {
      if (group.type === "table") {
        return (
          <TableBlock
            key={`tbl-${group.lines[0].num}`}
            lines={group.lines}
            highlightedNums={highlighted ? matchedHighlights : undefined}
            firstHighlightNum={firstHighlight}
            firstHighlightRef={firstHighlightRef}
          />
        );
      }
      return (
        <div key={group.line.num}>{renderLine(group.line as ClassifiedLine, highlighted)}</div>
      );
    });
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-white flex items-center justify-center">
        <div className="text-zinc-400 text-sm">Loading document…</div>
      </div>
    );
  }

  if (!data) {
    const notFound = error === "Document not found";
    return (
      <div className="fixed inset-0 bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-zinc-900 text-lg font-semibold mb-2">
            {notFound ? "Document not found" : "Failed to load document"}
          </div>
          <div className="text-zinc-500 text-sm">{error || "The requested document could not be loaded."}</div>
          <div className="text-zinc-400 text-xs mt-4 font-mono">{docId}</div>
        </div>
      </div>
    );
  }

  const { meta, source_type: sourceType } = data;
  const title = getDocumentTitle(meta, data.lines, sourceType);
  const authorStr = formatAuthors(meta.authors);
  const badge = getSourceBadge(sourceType, meta);
  const originalUrl = getOriginalUrl(meta, docId || "", sourceType);
  const sourceLabel = getSourceLabel(sourceType, meta);
  const metadataOnly = sourceType === "patents" && data.metadata_only === true;

  const trackSourceClick = (cta: CitationSourceCta) => {
    if (!docId || !originalUrl) return;
    trackEvent("citation_source_click", {
      citation_route: source,
      doc_id: docId,
      source_label: sourceLabel,
      cta,
      link_url: originalUrl,
      ...(user?.id && { gxl_user_id: user.id }),
    });
  };

  const drugName = meta.drug_name && meta.drug_name !== title ? meta.drug_name : undefined;
  const fdaAppNum = sourceType === "fda" ? getFdaAppNumber(meta, data.lines) : null;
  const fdaDocType = sourceType === "fda" ? formatFdaDocType(meta.source_type) : null;
  const subtitle = fdaAppNum || meta.identifier || meta.nct_id || meta.pub_number || meta.publication_number;

  return (
    <div ref={scrollContainerRef} data-scroll-container className="fixed inset-0 overflow-y-auto bg-white">
      <header className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-zinc-200">
        <div className="max-w-3xl mx-auto px-6 py-2.5 flex items-center justify-between">
          <a href="/" className="text-sm font-semibold text-zinc-900 hover:text-zinc-600 transition-colors">
            gxl
          </a>
          <div className="flex items-center gap-3 text-xs text-zinc-400">
            {badge && (
              <Badge variant="default" className="text-2xs pt-1 pb-0.25">{badge}</Badge>
            )}
            <span className="font-mono">{docId}</span>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 pt-10 pb-2">
        {originalUrl && (
          <a
            href={originalUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackSourceClick("header")}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-800 mb-4 transition-colors"
          >
            View on {sourceLabel}
          </a>
        )}
        {drugName && (
          <p className="text-xs font-medium text-blue-700 bg-blue-50 rounded px-2 py-1 mb-3 inline-block">
            {drugName}
          </p>
        )}
        <h1 className="font-display text-4xl font-normal text-zinc-900 leading-[1.15] mb-4 tracking-tight">
          {title || docId}
        </h1>
        {(subtitle || fdaDocType) && (
          <p className="text-sm text-zinc-500 font-mono mb-1">
            {fdaAppNum ? `Application ${subtitle}` : subtitle}
            {fdaDocType && subtitle && <span className="text-zinc-400 ml-2">· {fdaDocType}</span>}
            {fdaDocType && !subtitle && <span>{fdaDocType}</span>}
          </p>
        )}
        {authorStr && (
          <p className="text-base text-zinc-600 leading-relaxed mb-1">
            {authorStr}
          </p>
        )}
        {meta.month_year && (
          <p className="text-xs text-zinc-400 mb-1">{meta.month_year}</p>
        )}
        <div className="flex flex-wrap items-center gap-3 text-xs text-zinc-400 mt-2 mb-6">
          {meta.total_pages && <span>{meta.total_pages} pages</span>}
          {meta.journal && <span>{meta.journal}</span>}
          {meta.doi && (
            <a
              href={`https://doi.org/${meta.doi}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-zinc-500 hover:text-zinc-800 underline decoration-zinc-300"
            >
              doi:{meta.doi}
            </a>
          )}
          {meta.pmid && (
            <a
              href={`https://pubmed.ncbi.nlm.nih.gov/${meta.pmid}/`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-zinc-500 hover:text-zinc-800 underline decoration-zinc-300"
            >
              PMID:{meta.pmid}
            </a>
          )}
          {meta.phase && <span>Phase {meta.phase}</span>}
          {meta.status && <span>{meta.status}</span>}
        </div>
        {hasCitations && (
          <div className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-3 py-1.5 mb-4 inline-block">
            {matchedHighlights.size} line{matchedHighlights.size !== 1 ? "s" : ""} cited
            {unmatchedHighlights.length > 0 && (
              <span className="text-zinc-400 ml-1">
                ({unmatchedHighlights.map((n) => `L${n}`).join(", ")} not found)
              </span>
            )}
          </div>
        )}
      </div>

      {sourceType === "fda" && highlights.size > 0 ? (
        <div className="max-w-3xl mx-auto px-6 mb-8 pb-20">
          <FdaPdfViewer docId={docId || ""} highlights={highlights} />
        </div>
      ) : metadataOnly ? (
        <article className="max-w-3xl mx-auto px-6 pb-20">
          <PatentMetadataPanel meta={meta} />
        </article>
      ) : (
        <article className="max-w-3xl mx-auto px-6 pb-20">
          {hasCitations
            ? (() => {
              const allCited = [...highlights].sort((a, b) => a - b);
              const lineByNum = new Map(classified.map((l) => [l.num, l]));
              const items: React.ReactNode[] = [];
              let lastRenderedNum = 0;

              for (const n of allCited) {
                const line = lineByNum.get(n);

                const contextLines = classified.filter(
                  (l) => l.num > lastRenderedNum && l.num < n && !highlights.has(l.num) && !HEADER_ROLES.has(l.role)
                );
                if (contextLines.length > 0) {
                  items.push(
                    <ContextExpander
                      key={`ctx-before-${n}`}
                      lines={contextLines}
                      onRenderLines={(ctxLines) => renderGroupedLines(ctxLines, false)}
                    />
                  );
                }

                if (line) {
                  items.push(
                    <div key={`cited-${n}`} className="relative">
                      <span className="absolute -left-20 top-[2px] select-none text-xs font-mono font-medium text-amber-500 whitespace-nowrap">
                        Line {n}:
                      </span>
                      {renderLine(line, true)}
                    </div>
                  );
                  lastRenderedNum = n;
                } else {
                  items.push(
                    <div key={`missing-${n}`} className="relative">
                      <span className="absolute -left-20 top-[2px] select-none text-xs font-mono font-medium text-zinc-400 whitespace-nowrap">
                        Line {n}:
                      </span>
                      <p className="text-base text-zinc-400 leading-[1.7] mb-3 italic bg-zinc-50 border-l-[3px] border-l-zinc-300 pl-4 -ml-4 rounded-r py-1">
                        Line not found in document
                      </p>
                    </div>
                  );
                }
              }

              const trailingLines = classified.filter(
                (l) => l.num > lastRenderedNum && !highlights.has(l.num) && !HEADER_ROLES.has(l.role)
              );
              if (trailingLines.length > 0) {
                items.push(
                  <ContextExpander
                    key="ctx-trailing"
                    lines={trailingLines}
                    onRenderLines={(ctxLines) => renderGroupedLines(ctxLines, false)}
                  />
                );
              }

              return items;
            })()
            : renderGroupedLines(
              classified.filter((l) => !HEADER_ROLES.has(l.role)),
              false
            )}
        </article>
      )}
    </div>
  );
};
